import { useState } from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const [jumpValue, setJumpValue] = useState("");

  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i += 1) {
    pages.push(i);
  }

  const go = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const handleJump = () => {
    const num = Number(jumpValue);
    if (!Number.isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num);
      setJumpValue("");
    }
  };

  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => go(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-700 bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        aria-label="上一页"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {start > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(1)}
            className="w-9 h-9 rounded-md flex items-center justify-center text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
          >
            1
          </button>
          {start > 2 && <span className="px-1 text-foreground-400">...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => go(page)}
          className={`w-9 h-9 rounded-md flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
            page === currentPage
              ? "bg-primary-600 text-background-50"
              : "text-foreground-700 bg-background-100 hover:bg-background-200"
          }`}
        >
          {page}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && (
            <span className="px-1 text-foreground-400">...</span>
          )}
          <button
            type="button"
            onClick={() => go(totalPages)}
            className="w-9 h-9 rounded-md flex items-center justify-center text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => go(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-700 bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        aria-label="下一页"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>

      <div className="ml-2 flex items-center gap-2">
        <span className="text-sm text-foreground-600 hidden sm:inline">
          跳至
        </span>
        <input
          type="text"
          inputMode="numeric"
          value={jumpValue}
          onChange={(e) => setJumpValue(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleJump();
          }}
          className="w-12 h-9 text-center text-sm rounded-md bg-background-100 text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400"
          aria-label="跳转页码"
        />
        <span className="text-sm text-foreground-600 hidden sm:inline">页</span>
        <button
          type="button"
          onClick={handleJump}
          className="h-9 px-3 rounded-md text-sm font-medium bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          确定
        </button>
      </div>
    </div>
  );
}